import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ConfigQC.scss';
import DataGrid, { Column, Button as Butt, Editing, Item, KeyboardNavigation, Paging, Scrolling, Sorting, StateStoring, Toolbar, SearchPanel, Popup, Lookup, Position, RequiredRule, DataGridTypes, Selection, Export, Form, GroupPanel, Grouping } from 'devextreme-react/data-grid';
import { Item as FormItem, Label } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { Popup as DxPopup } from 'devextreme-react/popup';
import { SelectBox } from 'devextreme-react/select-box';
import { useApiConfigQC } from '../../api/api.configQC';
import { useApiConfiguration } from '../../api/api.configuration';
import { MaterialControlDTO, CreateMaterialControlDTO, LoteControlDTO, CreateLoteControlDTO, ConfiguracionQcDTO, CreateConfiguracionQcDTO } from '../../types/types.qualityControl';
import { useAppSelector } from '../../contexts/hooks';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import { ConvertLocalDate } from '../../utils/functions';
import { newToast } from '../../components';

const ConfigQC = () => {
  // Permisos
  const Prm_QC_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_R) ?? false;
  const Prm_QC_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_C) ?? false;
  const Prm_QC_U = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_U) ?? false;
  const Prm_QC_D = useAppSelector((x) => x.userInfo.Permissions?.Prm_QC_D) ?? false;

  // API Hooks
  const { getMaterials, getMaterialById, newMaterial, putMaterial, deleteMaterial, getLotsByMaterial, getLotById, newLot, putLot, deleteLot, getConfigurationsByLot, getConfigurationsBySede, getConfigurationById, newConfiguration, putConfiguration, deleteConfiguration } = useApiConfigQC();
  const { getAnalitos, getAnalizadores, getAnalytesByAnalyzer } = useApiConfiguration();

  // Referencias a los DataGrids
  const materialsGridRef = useRef<DataGrid>(null);
  const lotsGridRef = useRef<DataGrid>(null);
  const configurationsGridRef = useRef<DataGrid>(null);

  // Estado para datos de lookups
  const [materiales, setMateriales] = useState<MaterialControlDTO[]>([]);
  const [lotes, setLotes] = useState<LoteControlDTO[]>([]);
  const [analitos, setAnalitos] = useState<any[]>([]);
  const [analizadores, setAnalizadores] = useState<any[]>([]);
  const [sedes, setSedes] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [analitosFiltrados, setAnalitosFiltrados] = useState<any[]>([]);

  // Estado para el popup de configuración masiva
  const [showMassConfigPopup, setShowMassConfigPopup] = useState(false);
  const [massConfigLote, setMassConfigLote] = useState<number | null>(null);
  const [massConfigSede, setMassConfigSede] = useState<number | null>(null);
  const [massConfigAnalizador, setMassConfigAnalizador] = useState<number | null>(null);
  const [massConfigAnalitos, setMassConfigAnalitos] = useState<any[]>([]);
  const massConfigGridRef = useRef<DataGrid>(null);

  // Tipos para lookups
  const tiposMaterial = [
    { value: 'NORMAL', label: 'NORMAL' },
    { value: 'PATOLOGICO', label: 'PATOLÓGICO' },
  ];

  const nivelesMaterial = [
    { value: 'NIVEL 1', label: 'NIVEL 1' },
    { value: 'NIVEL 2', label: 'NIVEL 2' },
    { value: 'NIVEL 3', label: 'NIVEL 3' },
  ];

  useEffect(() => {
    // Cargar datos iniciales
    loadMaterials();
    loadLots();
    loadLookupData();
  }, []);

  const loadMaterials = async () => {
    const data = await getMaterials(true);
    setMateriales(data);
  };

  const loadLots = async () => {
    const materials = await getMaterials(true);
    const allLots: any[] = [];
    for (const material of materials) {
      const lots = await getLotsByMaterial(material.Qcm_Id, true);
      // Agregar información del material a cada lote para display descriptivo
      const lotsConMaterial = lots.map((lote: LoteControlDTO) => ({
        ...lote,
        Qcl_DisplayName: `${material.Qcm_Nombre}${material.Qcm_Nivel ? ' - ' + material.Qcm_Nivel : ''} - ${lote.Qcl_Codigo}`
      }));
      allLots.push(...lotsConMaterial);
    }
    setLotes(allLots);
  };

  // Obtener permisos de sedes del usuario
  const Sedes = useAppSelector((data) => data.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0].Data;

  const loadLookupData = async () => {
    try {
      const [analitosData, analizadoresData] = await Promise.all([getAnalitos(), getAnalizadores()]);

      // Preparar analitos con formato "ID - Nombre" para mejor visualización
      const analitosConDisplay = (analitosData || []).map((analito: any) => ({
        ...analito,
        An_DisplayName: `${analito.An_Id} - ${analito.An_Nombre}`,
      }));

      setAnalitos(analitosConDisplay);
      setAnalizadores(analizadoresData || []);

      setSedes(Sedes || []);
    } catch (error) {
      console.error('Error cargando datos de lookup:', error);
      setAnalitos([]);
      setAnalizadores([]);
      setSedes([]);
    }
  };

  // ============================================
  // MATERIALES DE CONTROL - DATA SOURCE
  // ============================================
  const materialsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcm_Id',
      load: () => {
        return getMaterials(true)
          .then((res) => ({
            data: res,
            totalCount: res.length,
          }))
          .catch(() => {
            throw new Error('Error al cargar materiales de control');
          });
      },
      insert: async (values) => {
        const material: Omit<CreateMaterialControlDTO, 'Qcm_Us_Id'> = {
          Qcm_Nombre: values.Qcm_Nombre,
          Qcm_Fabricante: values.Qcm_Fabricante,
          Qcm_Tipo: values.Qcm_Tipo,
          Qcm_Nivel: values.Qcm_Nivel,
          Qcm_Descripcion: values.Qcm_Descripcion,
        };
        const result = await newMaterial(material);
        if (result) {
          materialsGridRef.current?.instance.refresh();
          loadMaterials();
          return result;
        }
        throw new Error('Error al crear material');
      },
      update: async (key, values) => {
        // Obtener el registro completo actual
        const currentData = await getMaterialById(key);
        if (!currentData) {
          throw new Error('No se pudo obtener el material actual');
        }
        // Combinar datos actuales con los nuevos valores
        const completeData = {
          ...currentData,
          ...values,
        };
        const result = await putMaterial(key, completeData);
        if (result) {
          materialsGridRef.current?.instance.refresh();
          loadMaterials();
          return result;
        }
        throw new Error('Error al actualizar material');
      },
      remove: async (key) => {
        await deleteMaterial(key);
        materialsGridRef.current?.instance.refresh();
        loadMaterials();
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, []);

  // ============================================
  // LOTES DE CONTROL - DATA SOURCE
  // ============================================
  const lotsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcl_Id',
      load: () => {
        // Cargar todos los lotes de todos los materiales
        return getMaterials(true)
          .then(async (materials) => {
            const allLots: LoteControlDTO[] = [];
            for (const material of materials) {
              const lots = await getLotsByMaterial(material.Qcm_Id, true);
              allLots.push(...lots);
            }
            return {
              data: allLots,
              totalCount: allLots.length,
            };
          })
          .catch(() => {
            throw new Error('Error al cargar lotes de control');
          });
      },
      insert: async (values) => {
        const lote: Omit<CreateLoteControlDTO, 'Qcl_Us_Id'> = {
          Qcl_Qcm_Id: values.Qcl_Qcm_Id,
          Qcl_Codigo: values.Qcl_Codigo,
          Qcl_FechaRecepcion: ConvertLocalDate(values.Qcl_FechaRecepcion),
          Qcl_FechaVence: ConvertLocalDate(values.Qcl_FechaVence),
          Qcl_TempAlmacenamientoC: values.Qcl_TempAlmacenamientoC,
          Qcl_Observaciones: values.Qcl_Observaciones,
        };
        const result = await newLot(lote);
        if (result) {
          lotsGridRef.current?.instance.refresh();
          loadLots(); // Actualizar el estado de lotes para los Lookups
          return result;
        }
        throw new Error('Error al crear lote');
      },
      update: async (key, values) => {
        // Obtener el registro completo actual
        const currentData = await getLotById(key);
        if (!currentData) {
          throw new Error('No se pudo obtener el lote actual');
        }
        // Combinar datos actuales con los nuevos valores
        const completeData = {
          ...currentData,
          ...values,
        };
        const result = await putLot(key, completeData);
        if (result) {
          lotsGridRef.current?.instance.refresh();
          loadLots(); // Actualizar el estado de lotes para los Lookups
          return result;
        }
        throw new Error('Error al actualizar lote');
      },
      remove: async (key) => {
        await deleteLot(key);
        lotsGridRef.current?.instance.refresh();
        loadLots(); // Actualizar el estado de lotes para los Lookups
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, [materiales]);

  // ============================================
  // CONFIGURACIONES QC - DATA SOURCE
  // ============================================
  const configurationsDataSource = useMemo(() => {
    const customStore = new CustomStore({
      key: 'Qcc_Id',
      load: () => {
        // Cargar todas las configuraciones de todos los lotes
        return getMaterials(true)
          .then(async (materials) => {
            const allConfigs: ConfiguracionQcDTO[] = [];
            for (const material of materials) {
              const lots = await getLotsByMaterial(material.Qcm_Id, true);
              for (const lot of lots) {
                const configs = await getConfigurationsByLot(lot.Qcl_Id, true);
                allConfigs.push(...configs);
              }
            }
            return {
              data: allConfigs,
              totalCount: allConfigs.length,
            };
          })
          .catch(() => {
            throw new Error('Error al cargar configuraciones QC');
          });
      },
      insert: async (values) => {
        const configuracion: Omit<CreateConfiguracionQcDTO, 'Qcc_Us_Id'> = {
          Qcc_Qcl_Id: values.Qcc_Qcl_Id,
          Qcc_An_Id: values.Qcc_An_Id,
          Qcc_Az_Id: values.Qcc_Az_Id,
          Qcc_Sd_Id: values.Qcc_Sd_Id,
          Qcc_MediaEsperada: values.Qcc_MediaEsperada,
          Qcc_DesviacionEst: values.Qcc_DesviacionEst,
          Qcc_N: values.Qcc_N ?? 0,
          Qcc_PuntosGraficacion: values.Qcc_PuntosGraficacion,
          Qcc_FechaVigencia: ConvertLocalDate(values.Qcc_FechaVigencia),
          Qcc_FechaFin: values.Qcc_FechaFin ? ConvertLocalDate(values.Qcc_FechaFin) : values.Qcc_FechaFin,
        };
        const result = await newConfiguration(configuracion);
        if (result) {
          configurationsGridRef.current?.instance.refresh();
          return result;
        }
        throw new Error('Error al crear configuración');
      },
      update: async (key, values) => {
        // Obtener el registro completo actual
        const currentData = await getConfigurationById(key);
        if (!currentData) {
          throw new Error('No se pudo obtener la configuración actual');
        }
        // Combinar datos actuales con los nuevos valores
        const completeData = {
          ...currentData,
          ...values,
        };
        const result = await putConfiguration(key, completeData);
        if (result) {
          configurationsGridRef.current?.instance.refresh();
          return result;
        }
        throw new Error('Error al actualizar configuración');
      },
      remove: async (key) => {
        await deleteConfiguration(key);
        configurationsGridRef.current?.instance.refresh();
      },
    });

    return new DataSource({
      store: customStore,
      reshapeOnPush: true,
    });
  }, [materiales]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleRefreshMaterials = useCallback(() => {
    materialsGridRef.current?.instance.refresh();
    loadMaterials();
  }, []);

  const handleRefreshLots = useCallback(() => {
    lotsGridRef.current?.instance.refresh();
  }, []);

  const handleRefreshConfigurations = useCallback(() => {
    configurationsGridRef.current?.instance.refresh();
  }, []);

  const handleTabSelectionChanged = useCallback((e: any) => {
    setSelectedTab(e.addedItems[0].id);
  }, []);

  // Referencia para almacenar los analitos filtrados (para acceso inmediato en callbacks)
  const analitosFiltradosRef = useRef<any[]>([]);

  // Handler para cargar analitos cuando se selecciona un analizador
  const loadAnalitosByAnalizador = useCallback(
    async (analizadorId: number) => {
      if (!analizadorId) {
        setAnalitosFiltrados([]);
        analitosFiltradosRef.current = [];
        return [];
      }
      try {
        const analitosData = await getAnalytesByAnalyzer(analizadorId);
        // La API devuelve objetos con { Raa_An_Id, Analitos: { An_Id, An_Nombre } }
        // Mapeamos para extraer el analito y crear el displayName
        const analitosConDisplay = (analitosData || []).map((item: any) => {
          const analito = item.Analitos || item;
          return {
            An_Id: item.Raa_An_Id || analito.An_Id,
            An_Nombre: analito.An_Nombre,
            An_DisplayName: `${item.Raa_An_Id || analito.An_Id} - ${analito.An_Nombre || ''}`,
          };
        });
        setAnalitosFiltrados(analitosConDisplay);
        analitosFiltradosRef.current = analitosConDisplay;
        return analitosConDisplay;
      } catch (error) {
        console.error('Error cargando analitos por analizador:', error);
        setAnalitosFiltrados([]);
        analitosFiltradosRef.current = [];
        return [];
      }
    },
    [getAnalytesByAnalyzer]
  );

  // Handler para el evento onEditorPreparing del grid de configuraciones
  const onConfigEditorPreparing = useCallback(
    (e: any) => {
      if (e.parentType === 'dataRow' && e.dataField === 'Qcc_An_Id') {
        // Usar la referencia para obtener los datos más actuales
        const currentAnalitos = analitosFiltradosRef.current;
        e.editorOptions.dataSource = currentAnalitos;
        e.editorOptions.disabled = currentAnalitos.length === 0;

        // Guardar referencia al editor para poder actualizarlo después
        e.editorOptions.onInitialized = (args: any) => {
          (window as any).__analitoEditorInstance = args.component;
        };
      }
      if (e.parentType === 'dataRow' && e.dataField === 'Qcc_Az_Id') {
        const defaultOnValueChanged = e.editorOptions.onValueChanged;
        e.editorOptions.onValueChanged = async (args: any) => {
          // Llamar al handler original si existe
          if (defaultOnValueChanged) {
            defaultOnValueChanged(args);
          }
          e.setValue(args.value);

          // Cargar los analitos del analizador seleccionado
          const nuevosAnalitos = await loadAnalitosByAnalizador(args.value);

          // Obtener el editor del analito y actualizar su dataSource
          const analitoEditor = (window as any).__analitoEditorInstance;
          if (analitoEditor) {
            analitoEditor.option('dataSource', nuevosAnalitos);
            analitoEditor.option('disabled', nuevosAnalitos.length === 0);
            analitoEditor.option('value', null);
          }
        };
      }
    },
    [loadAnalitosByAnalizador]
  );

  // Handler para cuando se inicia la edición de una fila de configuración
  const onConfigEditingStart = useCallback(
    async (e: any) => {
      // Si hay un analizador seleccionado, cargar sus analitos
      if (e.data?.Qcc_Az_Id) {
        await loadAnalitosByAnalizador(e.data.Qcc_Az_Id);
      } else {
        setAnalitosFiltrados([]);
        analitosFiltradosRef.current = [];
      }
    },
    [loadAnalitosByAnalizador]
  );

  // Handler para cuando se inicia una nueva fila
  const onConfigInitNewRow = useCallback((e: any) => {
    e.data.Qcc_N = 0;
    e.data.Qcc_Estado = true;
    // Limpiar los analitos filtrados al crear nuevo registro
    setAnalitosFiltrados([]);
    analitosFiltradosRef.current = [];
  }, []);

  // Handler para validar antes de eliminar una configuración
  const onConfigRowRemoving = useCallback((e: any) => {
    if (e.data.CantidadMediciones && e.data.CantidadMediciones > 0) {
      e.cancel = true;
      newToast('ERROR', `No se puede eliminar la configuración porque tiene ${e.data.CantidadMediciones} mediciones asociadas`, 4);
    }
  }, []);

  // ============================================
  // CONFIGURACIÓN MASIVA DE ANALITOS
  // ============================================
  const handleOpenMassConfig = useCallback(() => {
    setMassConfigLote(null);
    setMassConfigSede(null);
    setMassConfigAnalizador(null);
    setMassConfigAnalitos([]);
    setShowMassConfigPopup(true);
  }, []);

  const handleCloseMassConfig = useCallback(() => {
    setShowMassConfigPopup(false);
    setMassConfigLote(null);
    setMassConfigSede(null);
    setMassConfigAnalizador(null);
    setMassConfigAnalitos([]);
  }, []);

  // Función para cargar analitos filtrando los ya configurados
  const loadAnalitosForMassConfig = useCallback(
    async (analizadorId: number, loteId: number | null, sedeId: number | null) => {
      if (!analizadorId) {
        setMassConfigAnalitos([]);
        return;
      }
      try {
        const analitosData = await getAnalytesByAnalyzer(analizadorId);

        // Obtener configuraciones existentes para este lote (si hay lote seleccionado)
        let configsExistentes: ConfiguracionQcDTO[] = [];
        if (loteId) {
          configsExistentes = await getConfigurationsByLot(loteId, true);
        }

        // Mapear los datos para el grid con campos editables
        let analitosParaGrid = (analitosData || []).map((item: any) => {
          const analito = item.Analitos || item;
          return {
            An_Id: item.Raa_An_Id || analito.An_Id,
            An_Nombre: analito.An_Nombre || '',
            Qcc_MediaEsperada: null,
            Qcc_DesviacionEst: null,
            Qcc_N: 0,
            Qcc_PuntosGraficacion: 20,
            Qcc_FechaVigencia: new Date(),
            Qcc_FechaFin: null,
          };
        });

        // Filtrar los analitos que ya están configurados para este lote+analizador+sede
        if (loteId && sedeId && configsExistentes.length > 0) {
          const analitosYaConfigurados = configsExistentes.filter((c) => c.Qcc_Az_Id === analizadorId && c.Qcc_Sd_Id === sedeId).map((c) => c.Qcc_An_Id);

          analitosParaGrid = analitosParaGrid.filter((a) => !analitosYaConfigurados.includes(a.An_Id));
        }

        setMassConfigAnalitos(analitosParaGrid);
      } catch (error) {
        console.error('Error cargando analitos:', error);
        setMassConfigAnalitos([]);
      }
    },
    [getAnalytesByAnalyzer, getConfigurationsByLot]
  );

  const handleAnalizadorChange = useCallback(
    async (analizadorId: number) => {
      setMassConfigAnalizador(analizadorId);
      await loadAnalitosForMassConfig(analizadorId, massConfigLote, massConfigSede);
    },
    [loadAnalitosForMassConfig, massConfigLote, massConfigSede]
  );

  // Recargar analitos cuando cambie el lote o sede
  const handleLoteChange = useCallback(
    async (loteId: number) => {
      setMassConfigLote(loteId);
      if (massConfigAnalizador) {
        await loadAnalitosForMassConfig(massConfigAnalizador, loteId, massConfigSede);
      }
    },
    [loadAnalitosForMassConfig, massConfigAnalizador, massConfigSede]
  );

  const handleSedeChange = useCallback(
    async (sedeId: number) => {
      setMassConfigSede(sedeId);
      if (massConfigAnalizador) {
        await loadAnalitosForMassConfig(massConfigAnalizador, massConfigLote, sedeId);
      }
    },
    [loadAnalitosForMassConfig, massConfigAnalizador, massConfigLote]
  );

  const handleSaveAnalitoConfig = useCallback(
    async (analitoData: any) => {
      if (!massConfigLote || !massConfigSede || !massConfigAnalizador) {
        newToast('ERROR', 'Debe seleccionar Lote, Sede y Analizador', 4);
        return;
      }
      if (!analitoData.Qcc_MediaEsperada || !analitoData.Qcc_DesviacionEst) {
        newToast('ERROR', 'Debe ingresar Media y Desviación Estándar', 4);
        return;
      }

      const configuracion: Omit<CreateConfiguracionQcDTO, 'Qcc_Us_Id'> = {
        Qcc_Qcl_Id: massConfigLote,
        Qcc_An_Id: analitoData.An_Id,
        Qcc_Az_Id: massConfigAnalizador,
        Qcc_Sd_Id: massConfigSede,
        Qcc_MediaEsperada: analitoData.Qcc_MediaEsperada,
        Qcc_DesviacionEst: analitoData.Qcc_DesviacionEst,
        Qcc_N: analitoData.Qcc_N ?? 0,
        Qcc_PuntosGraficacion: analitoData.Qcc_PuntosGraficacion ?? 20,
        Qcc_FechaVigencia: ConvertLocalDate(analitoData.Qcc_FechaVigencia),
        Qcc_FechaFin: analitoData.Qcc_FechaFin ? ConvertLocalDate(analitoData.Qcc_FechaFin) : analitoData.Qcc_FechaFin,
      };

      try {
        const result = await newConfiguration(configuracion);
        if (result) {
          newToast('SUCCESS', `Configuración guardada para ${analitoData.An_Nombre}`, 3);
          // Remover el analito del grid (ya está configurado)
          setMassConfigAnalitos((prev) => prev.filter((a) => a.An_Id !== analitoData.An_Id));
          // Refrescar el grid principal
          configurationsGridRef.current?.instance.refresh();
        }
      } catch (error) {
        console.error('Error guardando configuración:', error);
        newToast('ERROR', 'Error al guardar configuración', 4);
      }
    },
    [massConfigLote, massConfigSede, massConfigAnalizador, newConfiguration]
  );

  // ============================================
  // CELL PREPARE FUNCTIONS - Para colorear celdas según estado
  // ============================================
  const onLotCellPrepared = useCallback((e: any) => {
    if (e.rowType === 'data') {
      if (e.column.dataField === 'EstaVencido' && e.data.EstaVencido) {
        e.cellElement.style.backgroundColor = '#ffebee';
        e.cellElement.style.color = '#c62828';
        e.cellElement.style.fontWeight = 'bold';
      }
      if (e.column.dataField === 'DiasParaVencer' && e.data.DiasParaVencer <= 30) {
        e.cellElement.style.backgroundColor = '#fff3e0';
        e.cellElement.style.color = '#e65100';
      }
    }
  }, []);

  const onConfigCellPrepared = useCallback((e: any) => {
    if (e.rowType === 'data') {
      if (e.column.dataField === 'EstaVigente' && !e.data.EstaVigente) {
        e.cellElement.style.backgroundColor = '#f5f5f5';
        e.cellElement.style.color = '#9e9e9e';
      }
    }
  }, []);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <TabPanel selectedIndex={selectedTab} onSelectionChanged={handleTabSelectionChanged} swipeEnabled={false} animationEnabled={true} height='100%'>
          {/* ============================================ */}
          {/* TAB 1: MATERIALES DE CONTROL */}
          {/* ============================================ */}
          <TabItem title='Materiales de Control'>
            <DataGrid
              className='grid'
              dataSource={materialsDataSource}
              ref={materialsGridRef}
              width={'100%'}
              hoverStateEnabled={true}
              height='100vh'
              repaintChangesOnly={true}
              highlightChanges={true}
              rowAlternationEnabled={true}
              showColumnLines={true}
              onInitNewRow={(e) => {
                e.data.Qcm_Estado = true;
              }}>
              <SearchPanel visible={true} />
              <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='single' />
              <Editing
                mode='popup'
                allowUpdating={Prm_QC_U}
                allowAdding={Prm_QC_C}
                allowDeleting={Prm_QC_D}
                selectTextOnEditStart={true}
                texts={{
                  saveRowChanges: 'Guardar',
                  cancelRowChanges: 'Cancelar',
                  deleteRow: 'Eliminar',
                  addRow: 'Agregar',
                }}>
                <Popup title='MATERIAL DE CONTROL' showTitle={true} width={700} height={500}>
                  <Position my='center' at='center' of={window} />
                </Popup>
                <Form colCount={1}>
                  <FormItem dataField='Qcm_Id' visible={false} />
                  <FormItem dataField='Qcm_Nombre' editorType='dxTextBox'>
                    <Label text='NOMBRE' />
                  </FormItem>
                  <FormItem dataField='Qcm_Fabricante' editorType='dxTextBox'>
                    <Label text='FABRICANTE' />
                  </FormItem>
                  <FormItem dataField='Qcm_Tipo' editorType='dxSelectBox'>
                    <Label text='TIPO' />
                  </FormItem>
                  <FormItem dataField='Qcm_Nivel' editorType='dxSelectBox'>
                    <Label text='NIVEL' />
                  </FormItem>
                  <FormItem dataField='Qcm_Descripcion' editorType='dxTextArea'>
                    <Label text='DESCRIPCIÓN' />
                  </FormItem>
                  <FormItem dataField='Qcm_Estado' editorType='dxCheckBox'>
                    <Label text='ESTADO' />
                  </FormItem>
                  <FormItem dataField='CantidadLotesActivos' visible={false} />
                </Form>
              </Editing>
              <Paging enabled={false} />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-header'>Materiales de Control</div>
                </Item>
                <Item name='searchPanel' />
                <Item name='addRowButton' />
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='RefreshMaterials' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefreshMaterials} />
                  <Tooltip target={'#RefreshMaterials'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Refrescar datos
                  </Tooltip>
                </Item>
              </Toolbar>
              <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Qcm_Id' width={90} alignment='center' name='Qcm_Id' />
              <Column allowGrouping={false} caption='NOMBRE' visible={true} allowEditing={true} dataField='Qcm_Nombre' alignment='left' minWidth={200}>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='FABRICANTE' allowEditing={true} allowSorting={false} dataField='Qcm_Fabricante' width={150} alignment='left' />
              <Column allowGrouping={false} caption='TIPO' visible={true} allowEditing={true} dataField='Qcm_Tipo' width={120} alignment='center'>
                <Lookup dataSource={tiposMaterial} valueExpr='value' displayExpr='label' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='NIVEL' visible={true} allowEditing={true} dataField='Qcm_Nivel' width={100} alignment='center'>
                <Lookup dataSource={nivelesMaterial} valueExpr='value' displayExpr='label' allowClearing={true} />
              </Column>
              <Column allowGrouping={false} caption='DESCRIPCIÓN' visible={false} allowEditing={true} dataField='Qcm_Descripcion' alignment='left' />
              <Column allowGrouping={false} allowSorting={false} caption='LOTES ACTIVOS' visible={true} showInColumnChooser={false} allowEditing={false} dataField='CantidadLotesActivos' width={120} alignment='center' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='Qcm_Estado' alignment='center' width={90} dataType='boolean' />

              <Column type='buttons' caption='ACCIÓN' width={100}>
                <Butt name='edit' />
                <Butt name='delete' />
              </Column>
            </DataGrid>
          </TabItem>

          {/* ============================================ */}
          {/* TAB 2: LOTES DE CONTROL */}
          {/* ============================================ */}
          <TabItem title='Lotes de Control'>
            <DataGrid
              className='grid'
              dataSource={lotsDataSource}
              ref={lotsGridRef}
              width={'100%'}
              hoverStateEnabled={true}
              height='100vh'
              repaintChangesOnly={true}
              highlightChanges={true}
              rowAlternationEnabled={true}
              showColumnLines={true}
              onCellPrepared={onLotCellPrepared}
              onInitNewRow={(e) => {
                e.data.Qcl_Estado = true;
              }}>
              <SearchPanel visible={true} />
              <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='single' />
              <Editing
                mode='popup'
                allowUpdating={Prm_QC_U}
                allowAdding={Prm_QC_C}
                allowDeleting={Prm_QC_D}
                selectTextOnEditStart={true}
                texts={{
                  saveRowChanges: 'Guardar',
                  cancelRowChanges: 'Cancelar',
                  deleteRow: 'Eliminar',
                  addRow: 'Agregar',
                }}>
                <Popup title='LOTE DE CONTROL' showTitle={true} width={750} height={520}>
                  <Position my='center' at='center' of={window} />
                </Popup>
                <Form colCount={2}>
                  <FormItem dataField='Qcl_Id' visible={false} />
                  <FormItem dataField='Qcl_Qcm_Id' editorType='dxSelectBox' colSpan={2}>
                    <Label text='MATERIAL' />
                  </FormItem>
                  <FormItem dataField='Qcl_Codigo' editorType='dxTextBox' colSpan={2}>
                    <Label text='CÓDIGO LOTE' />
                  </FormItem>
                  <FormItem dataField='Qcl_FechaRecepcion' editorType='dxDateBox'>
                    <Label text='F. RECEPCIÓN' />
                  </FormItem>
                  <FormItem dataField='Qcl_FechaVence' editorType='dxDateBox'>
                    <Label text='F. VENCIMIENTO' />
                  </FormItem>
                  <FormItem dataField='Qcl_TempAlmacenamientoC' editorType='dxNumberBox' colSpan={2}>
                    <Label text='TEMP. °C' />
                  </FormItem>
                  <FormItem dataField='Qcl_Observaciones' editorType='dxTextArea' colSpan={2}>
                    <Label text='OBSERVACIONES' />
                  </FormItem>
                  <FormItem dataField='Qcl_Estado' editorType='dxCheckBox' colSpan={2}>
                    <Label text='ESTADO' />
                  </FormItem>
                  <FormItem dataField='MaterialTipo' visible={false} />
                  <FormItem dataField='DiasParaVencer' visible={false} />
                  <FormItem dataField='EstaVencido' visible={false} />
                  <FormItem dataField='CantidadConfiguraciones' visible={false} />
                </Form>
              </Editing>
              <Paging enabled={false} />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-header'>Lotes de Control</div>
                </Item>
                <Item name='searchPanel' />
                <Item name='addRowButton' />
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='RefreshLots' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefreshLots} />
                  <Tooltip target={'#RefreshLots'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Refrescar datos
                  </Tooltip>
                </Item>
              </Toolbar>
              <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Qcl_Id' width={90} alignment='center' name='Qcl_Id' />
              <Column allowGrouping={false} caption='MATERIAL' visible={true} allowEditing={true} dataField='Qcl_Qcm_Id' alignment='left'>
                <Lookup dataSource={materiales} valueExpr='Qcm_Id' displayExpr='Qcm_Nombre' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} allowSorting={false} caption='TIPO MATERIAL' visible={true} showInColumnChooser={false} allowEditing={false} dataField='MaterialTipo' width={120} alignment='center' />
              <Column allowGrouping={false} caption='CÓDIGO LOTE' visible={true} allowEditing={true} dataField='Qcl_Codigo' alignment='left' width={150}>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='F. RECEPCIÓN' visible={true} allowEditing={true} dataField='Qcl_FechaRecepcion' width={130} alignment='center' dataType='date' format='dd/MM/yyyy'>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='F. VENCIMIENTO' visible={true} allowEditing={true} dataField='Qcl_FechaVence' width={130} alignment='center' dataType='date' format='dd/MM/yyyy'>
                <RequiredRule />
              </Column>
              <Column
                allowGrouping={false}
                allowSorting={false}
                caption='DÍAS RESTANTES'
                visible={true}
                showInColumnChooser={false}
                allowEditing={false}
                dataField='DiasParaVencer'
                width={130}
                alignment='center'
                cellRender={(data) => {
                  if (data.value < 0) return <span style={{ color: '#c62828', fontWeight: 'bold' }}>VENCIDO</span>;
                  if (data.value <= 30) return <span style={{ color: '#e65100', fontWeight: 'bold' }}>{data.value}</span>;
                  return <span style={{ color: '#2e7d32' }}>{data.value}</span>;
                }}
              />
              <Column allowGrouping={false} allowSorting={false} caption='TEMP. °C' visible={false} allowEditing={true} dataField='Qcl_TempAlmacenamientoC' width={90} alignment='center' dataType='number' />
              <Column allowGrouping={false} caption='OBSERVACIONES' visible={false} allowEditing={true} dataField='Qcl_Observaciones' alignment='left' />
              <Column allowGrouping={false} allowSorting={false} caption='CONFIGS.' visible={true} showInColumnChooser={false} allowEditing={false} dataField='CantidadConfiguraciones' width={100} alignment='center' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='Qcl_Estado' alignment='center' width={90} dataType='boolean' />

              <Column type='buttons' caption='ACCIÓN' width={100}>
                <Butt name='edit' />
                <Butt name='delete' />
              </Column>
            </DataGrid>
          </TabItem>

          {/* ============================================ */}
          {/* TAB 3: CONFIGURACIONES QC */}
          {/* ============================================ */}
          <TabItem title='Configuraciones QC'>
            <DataGrid className='grid config-qc-grid' dataSource={configurationsDataSource} ref={configurationsGridRef} width={'100%'} hoverStateEnabled={true} height='100vh' repaintChangesOnly={true} highlightChanges={true} rowAlternationEnabled={true} showColumnLines={true} onCellPrepared={onConfigCellPrepared} onInitNewRow={onConfigInitNewRow} onEditingStart={onConfigEditingStart} onEditorPreparing={onConfigEditorPreparing} onRowRemoving={onConfigRowRemoving}>
              <SearchPanel visible={true} />
              <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} showScrollbar='always' />
              <Sorting mode='multiple' />
              <Selection mode='single' />
              <GroupPanel visible={false} />
              <Grouping autoExpandAll={true} allowCollapsing={true} />
              <Editing
                mode='popup'
                allowUpdating={Prm_QC_U}
                allowAdding={Prm_QC_C}
                allowDeleting={Prm_QC_D}
                selectTextOnEditStart={true}
                texts={{
                  saveRowChanges: 'Guardar',
                  cancelRowChanges: 'Cancelar',
                  deleteRow: 'Eliminar',
                  addRow: 'Agregar',
                }}>
                <Popup title='CONFIGURACIÓN DE CONTROL DE CALIDAD' showTitle={true} width={850} height={580}>
                  <Position my='center' at='center' of={window} />
                </Popup>
                <Form colCount={2}>
                  <FormItem dataField='Qcc_Id' visible={false} />
                  <FormItem dataField='Qcc_Qcl_Id' editorType='dxSelectBox'>
                    <Label text='LOTE' />
                  </FormItem>
                  <FormItem dataField='Qcc_Sd_Id' editorType='dxSelectBox'>
                    <Label text='SEDE' />
                  </FormItem>
                  <FormItem dataField='Qcc_Az_Id' editorType='dxSelectBox'>
                    <Label text='ANALIZADOR' />
                  </FormItem>
                  <FormItem dataField='Qcc_An_Id' editorType='dxSelectBox'>
                    <Label text='ANALITO' />
                  </FormItem>
                  <FormItem dataField='Qcc_MediaEsperada' editorType='dxNumberBox'>
                    <Label text='MEDIA' />
                  </FormItem>
                  <FormItem dataField='Qcc_DesviacionEst' editorType='dxNumberBox'>
                    <Label text='DESV. ESTÁNDAR' />
                  </FormItem>
                  <FormItem dataField='Qcc_N' editorType='dxNumberBox' editorOptions={{ value: 0, showClearButton: true }}>
                    <Label text='N (MEDICIONES)' />
                  </FormItem>
                  <FormItem dataField='Qcc_PuntosGraficacion' editorType='dxNumberBox'>
                    <Label text='PUNTOS GRAFICACIÓN' />
                  </FormItem>
                  <FormItem dataField='Qcc_FechaVigencia' editorType='dxDateBox'>
                    <Label text='F. VIGENCIA' />
                  </FormItem>
                  <FormItem dataField='Qcc_FechaFin' editorType='dxDateBox'>
                    <Label text='F. FIN' />
                  </FormItem>
                  <FormItem dataField='Qcc_Estado' editorType='dxCheckBox' colSpan={2} editorOptions={{ value: true }}>
                    <Label text='ESTADO' />
                  </FormItem>
                  <FormItem dataField='LoteCodigo' visible={false} />
                  <FormItem dataField='MaterialNombre' visible={false} />
                  <FormItem dataField='MaterialNivel' visible={false} />
                  <FormItem dataField='AnalitoNombre' visible={false} />
                  <FormItem dataField='AnalizadorNombre' visible={false} />
                  <FormItem dataField='SedeNombre' visible={false} />
                  <FormItem dataField='EstaVigente' visible={false} />
                  <FormItem dataField='CantidadMediciones' visible={false} />
                </Form>
              </Editing>
              <Paging enabled={false} />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-header'>Configuraciones QC</div>
                </Item>
                <Item name='searchPanel' />
                <Item name='addRowButton' />
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='MassConfig' icon='bulletlist' text='Configuración Masiva' stylingMode='text' onClick={handleOpenMassConfig} />
                  <Tooltip target={'#MassConfig'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Configurar múltiples analitos por analizador
                  </Tooltip>
                </Item>
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='RefreshConfigs' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefreshConfigurations} />
                  <Tooltip target={'#RefreshConfigs'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Refrescar datos
                  </Tooltip>
                </Item>
              </Toolbar>
              <KeyboardNavigation editOnKeyPress={true} enterKeyAction='startEdit' enterKeyDirection='column' />
              <StateStoring enabled={true} type='custom' />

              <Column allowGrouping={false} allowSorting={false} caption='ID' allowEditing={false} visible={true} dataField='Qcc_Id' width={50} alignment='center' name='Qcc_Id' />
              <Column 
                allowGrouping={true} 
                allowSorting={false} 
                caption='MATERIAL' 
                visible={true} 
                showInColumnChooser={false} 
                allowEditing={false} 
                dataField='MaterialNombre' 
                minWidth={280}
                alignment='left'
                groupIndex={1}
                calculateCellValue={(rowData: any) => {
                  const nombre = rowData.MaterialNombre || '';
                  const nivel = rowData.MaterialNivel || '';
                  return nivel ? `${nombre} - ${nivel}` : nombre;
                }}
              />
              <Column allowGrouping={false} allowSorting={false} caption='LOTE' visible={true} showInColumnChooser={false} allowEditing={false} dataField='LoteCodigo' width={130} alignment='left' />
              <Column allowGrouping={false} caption='LOTE' visible={false} allowEditing={true} dataField='Qcc_Qcl_Id' width={180} alignment='left'>
                <Lookup dataSource={lotes} valueExpr='Qcl_Id' displayExpr='Qcl_DisplayName' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} allowSorting={false} caption='ANALITO' visible={true} showInColumnChooser={false} allowEditing={false} dataField='AnalitoNombre' minWidth={300} alignment='left' />
              <Column allowGrouping={false} caption='ANALITO' visible={false} allowEditing={true} dataField='Qcc_An_Id' width={300} alignment='center'>
                <Lookup dataSource={analitosFiltrados.length > 0 ? analitosFiltrados : analitos} valueExpr='An_Id' displayExpr='An_DisplayName' />
                <RequiredRule />
              </Column>
              <Column 
                allowGrouping={true} 
                allowSorting={false} 
                caption='ANALIZADOR' 
                visible={true} 
                showInColumnChooser={false} 
                allowEditing={false} 
                dataField='AnalizadorNombre' 
                width={150} 
                alignment='left'
                groupIndex={0}
              />
              <Column allowGrouping={false} caption='ANALIZADOR' visible={false} allowEditing={true} dataField='Qcc_Az_Id' width={100} alignment='center'>
                <Lookup dataSource={analizadores} valueExpr='Az_Id' displayExpr='Az_Nombre' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} allowSorting={false} caption='SEDE' visible={false} showInColumnChooser={false} allowEditing={false} dataField='SedeNombre' width={120} alignment='left' />
              <Column allowGrouping={false} caption='SEDE' visible={false} allowEditing={true} dataField='Qcc_Sd_Id' width={100} alignment='center' >
                <Lookup dataSource={sedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='MEDIA' visible={true} allowEditing={true} dataField='Qcc_MediaEsperada' width={100} alignment='center' dataType='number' format='#,##0.00'>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='DESV. EST.' visible={true} allowEditing={true} dataField='Qcc_DesviacionEst' width={100} alignment='center' dataType='number' format='#,##0.00'>
                <RequiredRule />
              </Column>
              <Column
                allowGrouping={false}
                caption='N'
                visible={true}
                allowEditing={true}
                dataField='Qcc_N'
                width={90}
                alignment='center'
                dataType='number'
                setCellValue={(newData: any, value: any) => {
                  newData.Qcc_N = value ?? 0;
                }}
              />

              <Column allowGrouping={false} caption='PUNTOS GRAFICACIÓN' visible={false} allowEditing={true} dataField='Qcc_PuntosGraficacion' width={100} alignment='center' dataType='number'>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='F. VIGENCIA' visible={true} allowEditing={true} dataField='Qcc_FechaVigencia' width={120} alignment='center' dataType='date' format='dd/MM/yyyy'>
                <RequiredRule />
              </Column>
              <Column allowGrouping={false} caption='F. FIN' visible={false} allowEditing={true} dataField='Qcc_FechaFin' width={120} alignment='center' dataType='date' format='dd/MM/yyyy' />
              <Column allowGrouping={false} allowSorting={false} caption='VIGENTE' visible={true} showInColumnChooser={false} allowEditing={false} dataField='EstaVigente' width={80} alignment='center' dataType='boolean' />
              <Column allowGrouping={false} allowSorting={false} caption='MEDICIONES' visible={true} showInColumnChooser={false} allowEditing={false} dataField='CantidadMediciones' width={100} alignment='center' />
              <Column allowGrouping={false} allowSorting={false} caption='ESTADO' allowEditing={true} dataField='Qcc_Estado' alignment='center' width={90} dataType='boolean' />

              <Column type='buttons' caption='ACCIÓN' width={100}>
                <Butt name='edit' />
                <Butt name='delete' />
              </Column>
            </DataGrid>
          </TabItem>
        </TabPanel>

        {/* ============================================ */}
        {/* POPUP DE CONFIGURACIÓN MASIVA */}
        {/* ============================================ */}
        <DxPopup visible={showMassConfigPopup} onHiding={handleCloseMassConfig} title='CONFIGURACIÓN MASIVA DE ANALITOS' showCloseButton={true} height='90%' width={'90%'}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '10px' }}>
            {/* Selectores superiores */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>SEDE *</label>
                <SelectBox dataSource={sedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' value={massConfigSede} onValueChanged={(e) => handleSedeChange(e.value)} placeholder='Seleccionar sede...' searchEnabled={true} showClearButton={true} height={32} />
              </div>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>ANALIZADOR *</label>
                <SelectBox dataSource={analizadores} valueExpr='Az_Id' displayExpr='Az_Nombre' value={massConfigAnalizador} onValueChanged={(e) => handleAnalizadorChange(e.value)} placeholder='Seleccionar analizador...' searchEnabled={true} showClearButton={true} height={32} />
              </div>
              <div style={{ flex: '1', minWidth: '180px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '3px', display: 'block', fontSize: '12px' }}>LOTE *</label>
                <SelectBox dataSource={lotes} valueExpr='Qcl_Id' displayExpr='Qcl_DisplayName' value={massConfigLote} onValueChanged={(e) => handleLoteChange(e.value)} placeholder='Seleccionar lote...' searchEnabled={true} showClearButton={true} height={32} />
              </div>
            </div>

            {/* Grid de analitos */}
            <div style={{ flex: 1, minHeight: 0 }}>
              {massConfigAnalitos.length > 0 ? (
                <DataGrid
                  ref={massConfigGridRef}
                  dataSource={massConfigAnalitos}
                  keyExpr='An_Id'
                  height='100%'
                  showBorders={true}
                  rowAlternationEnabled={true}
                  hoverStateEnabled={true}
                  columnAutoWidth={true}
                  onRowUpdated={(e) => {
                    // Actualizar el estado cuando se edita una fila
                    setMassConfigAnalitos((prev) => prev.map((a) => (a.An_Id === e.key ? { ...a, ...e.data } : a)));
                  }}
                  onRowPrepared={(e) => {
                    // Hacer las filas más delgadas
                    if (e.rowType === 'data' || e.rowType === 'header') {
                      e.rowElement.style.height = '32px';
                    }
                  }}>
                  <SearchPanel visible={true} />
                  <Editing mode='cell' allowUpdating={true} />
                  <Scrolling mode='virtual' />
                  <Column dataField='An_Id' caption='ID' width={70} allowEditing={false} alignment='center' />
                  <Column dataField='An_Nombre' caption='ANALITO' minWidth={180} allowEditing={false} />
                  <Column dataField='Qcc_MediaEsperada' caption='MEDIA *' width={110} dataType='number' format='#,##0.00' alignment='center'>
                    <RequiredRule />
                  </Column>
                  <Column dataField='Qcc_DesviacionEst' caption='DESV. EST. *' width={110} dataType='number' format='#,##0.00' alignment='center'>
                    <RequiredRule />
                  </Column>
                  <Column dataField='Qcc_N' caption='N' width={60} dataType='number' alignment='center' />
                  <Column dataField='Qcc_PuntosGraficacion' caption='PUNTOS' width={110} dataType='number' alignment='center' />
                  <Column dataField='Qcc_FechaVigencia' caption='F. VIGENCIA' width={110} dataType='date' format='dd/MM/yyyy' alignment='center' />
                  <Column dataField='Qcc_FechaFin' caption='F. FIN' width={110} dataType='date' format='dd/MM/yyyy' alignment='center' />
                  <Column type='buttons' caption='' width={50}>
                    <Butt icon='save' hint='Guardar configuración' onClick={(e: any) => handleSaveAnalitoConfig(e.row.data)} />
                  </Column>
                </DataGrid>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#666',
                    fontSize: '16px',
                  }}>
                  {massConfigAnalizador ? 'No hay analitos configurados para este analizador' : 'Seleccione un analizador para ver sus analitos'}
                </div>
              )}
            </div>
          </div>
        </DxPopup>
      </div>
    </div>
  );
};

export default ConfigQC;
